// frontend/src/hooks/useMigrateNotes.ts
/**
 * Hook to lazily migrate legacy notes to timeline entries.
 *
 * Fires once on mount when a livestockId is provided.
 * The backend endpoint is idempotent, so calling it multiple times
 * is safe and will only create an entry on the first call.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTank } from '@/contexts/TankContext';
import { timelineApi } from '@/api/timeline';

export function useMigrateNotes(livestockId: number | undefined) {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['migrate-notes', selectedTank?.id, livestockId],
    queryFn: async () => {
      const result = await timelineApi.migrateNotes(selectedTank!.id, livestockId!);

      // If migration happened, invalidate the timeline cache so the new entry appears
      if (result.migrated) {
        queryClient.invalidateQueries({
          queryKey: ['timeline', selectedTank?.id, livestockId],
        });
      }

      return result;
    },
    enabled: !!selectedTank?.id && !!livestockId,
    // Only run once per session per organism - no refetching
    staleTime: Infinity,
    retry: false,
  });
}
